import { useLoaderData, Link } from '@remix-run/react'
import { json } from '@remix-run/node'
import invariant from 'tiny-invariant'
import { prisma } from '~/utils/db.server.ts'
import { getUserId } from '~/utils/auth.server.ts'
import { getLinkPreviews } from '~/utils/links.server.ts'
import RecipeList from '~/components/Recipes/RecipeList.tsx'
import LinkList from '~/components/Links/LinkList.tsx'
import { getUserImgSrc } from '~/utils/misc.ts'
import { EditProfileButton } from '~/components/Buttons/Buttons.tsx'

export async function loader({ params, request }: DataFunctionArgs) {
	const userId = await getUserId(request)
	invariant(params.username, 'Missing username')
	// console.log('request :>> ', request)
	const user = await prisma.user.findUnique({
		where: { username: params.username },
		select: {
			id: true,
			username: true,
			name: true,
			imageId: true,
			bio: true,
			createdAt: true,
			recipes: {
				select: {
					id: true,
					title: true,
					slug: true,
					imageId: true,
					owner: {
						select: {
							id: true,
							name: true,
							username: true,
						},
					},
				},
				take: 4,
			},
		},
	})
	if (!user) {
		throw new Response('No data found for that user', { status: 404 })
	}

	const isOwnProfile = user.id === userId

	const linkPreviews = await getLinkPreviews(params.username, 4)

	// console.log('linkPreviews :>> ', linkPreviews)

	return json({
		isOwnProfile,
		user,
		userJoinedDisplay: user.createdAt.toLocaleDateString(),
		linkPreviews,
	})
}

export default function UsernameIndexRoute() {
	const { user, isOwnProfile, linkPreviews } = useLoaderData<typeof loader>()
	const { username, name, recipes, bio } = user
	const userDisplayName = name ?? username
	return (
		<>
			<div className="container-lg mx-auto mb-16 mt-4 flex flex-col sm:flex-row">
				<div className="relative mx-auto sm:ml-12 sm:w-40">
					<img
						src={getUserImgSrc(user.imageId)}
						alt={userDisplayName}
						className="h-40 w-40 rounded-full object-cover"
					/>
				</div>
				<div className="min-w-md max-w-md flex-col justify-center gap-4 sm:flex sm:pl-8">
					<div
						className={
							bio ? 'items-center pb-8 sm:flex' : 'flex-col gap-4 pb-8 sm:flex'
						}
					>
						<h1 className="mt-2 text-center text-2xl sm:basis-2/3 sm:text-left">
							{userDisplayName}
						</h1>
						{isOwnProfile && (
							<div className="mx-auto mt-2 w-fit min-w-fit">
								<EditProfileButton username={username} />
							</div>
						)}
					</div>
					{bio ? <p>{bio}</p> : null}
					{/* TODO: Add location and bio to user */}
					{/* <p>Anywhere, USA</p> */}
					{/* https://developers.google.com/maps/documentation/places/web-service/autocomplete */}
				</div>
			</div>
			{recipes.length > 0 ? (
				<div className="w-full lg:w-3/4">
					{recipes.length > 3 ? (
						<div className="">
							<Link
								to={`/${username}/recipes`}
								className="transition-colors hover:text-primary-bright"
							>
								<h2 className="mb-4 border-y py-4 text-center text-3xl font-medium sm:mb-6 sm:border-t-0 sm:pt-8">
									Latest Recipes
								</h2>
							</Link>
						</div>
					) : (
						<h2 className="mb-4 border-y py-4 text-center  text-3xl font-medium sm:mb-6 sm:border-t-0 sm:pt-8">
							Latest Recipes
						</h2>
					)}
					<RecipeList recipes={recipes.slice(0, 3)} showUser={false} />
					{recipes.length > 3 ? (
						<div className="">
							<Link
								to={`/${username}/recipes`}
								className="mt-8 inline-block w-full text-right text-lg font-medium text-foreground transition-colors hover:text-primary-bright"
							>
								all @{username}'s recipes
							</Link>
						</div>
					) : null}
				</div>
			) : (
				<>
					<p className="mb-8 text-6xl">🤔</p>
					<p className="max-w-sm text-center text-2xl">
						{isOwnProfile
							? `No recipes found. Better add one or people might think you're a bot! 🤖`
							: `${userDisplayName} hasn't added any recipes yet. Check back later!`}
					</p>
				</>
			)}
			{linkPreviews.length > 0 ? (
				<div className="mt-4 w-full lg:w-3/4">
					{linkPreviews.length > 3 ? (
						<Link
							to={`/${username}/links`}
							className="transition-colors hover:text-primary-bright"
						>
							<h2 className="mb-4 border-y py-4 text-center text-3xl font-medium sm:mb-6 sm:border-t-0 sm:pt-8">
								Latest Links
							</h2>
						</Link>
					) : (
						<h2 className="mb-4 border-y py-4 text-center text-3xl font-medium sm:mb-6 sm:border-t-0 sm:pt-8">
							Latest Links
						</h2>
					)}
					<LinkList linkPreviews={linkPreviews.slice(0, 3)} />
					{linkPreviews.length > 3 ? (
						<div className="">
							<Link
								to={`/${username}/links`}
								className="mt-8 inline-block w-full text-right text-lg font-medium text-foreground transition-colors hover:text-primary-bright"
							>
								all @{username}'s links
							</Link>
						</div>
					) : null}
				</div>
			) : null}
		</>
	)
}
